import { Box, List, ListItem, Typography, Divider } from '@mui/material';
import dayjs from 'dayjs';
import numeral from 'numeral';
import { type Observation } from '../../../shared/types';
import { useForecastHours } from '../../hooks/useForecasts';
import { WeatherIcon } from '../WeatherIcon';

type Props = {
    observation: Observation;
};

export const Today = ({ observation }: Props) => {
    // Held to the top of the hour so the query key stays stable between renders.
    const { data: forecastHours = [] } = useForecastHours(observation.station._id.toString(), dayjs().startOf('hour').toISOString());
    const [currentHour] = forecastHours;

    return (
        <List>
            <ListItem dense={true}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>Currently:</Typography>
                <Typography variant="h5">{numeral(observation.min15.avgTemp).format('0.0')}&#8457;</Typography>
            </ListItem>
            {currentHour?.description && (
                <ListItem dense={true}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Conditions:`}</Typography>
                    {currentHour.icon && (
                        <Box sx={{ display: 'flex', mr: 0.5 }}>
                            <WeatherIcon icon={currentHour.icon} description={currentHour.description} />
                        </Box>
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {currentHour.description}
                    </Typography>
                </ListItem>
            )}
            <ListItem dense={true}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>{`High | Low:`}</Typography>
                <Typography variant="body1" color="error">
                    {numeral(observation.min15.runningMaxTemp).format('0.0')}&#8457;
                </Typography>
                <Divider orientation="vertical" flexItem={true} sx={{ ml: 1, mr: 1 }} />
                <Typography variant="body1" color="info">
                    {numeral(observation.min15.runningMinTemp).format('0.0')}&#8457;
                </Typography>
            </ListItem>
            <Divider />
            <ListItem dense={true} sx={{ pt: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>{`15min PCPN:`}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {`${numeral(observation.min15['15minPcpn']).format('0.00')} IN`}
                </Typography>
            </ListItem>
            <ListItem dense={true}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Since 12am:`}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {`${numeral(observation.min15.runningTotalPcpn).format('0.00')} IN`}
                </Typography>
            </ListItem>
            <ListItem dense={true}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Humidity:`}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {`${numeral(observation.min15.avgRH).format('0')}%`}
                </Typography>
            </ListItem>
            <ListItem dense={true}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Dew Point:`}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {`${numeral(observation.min15.avgDewPointTemp).format('0.0')}`}&#176;
                </Typography>
            </ListItem>
            <ListItem dense={true}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Wind:`}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {`${observation.min15.avgWindDir} ${numeral(observation.min15.avgWindSpeed).format('0')} MPH`}
                </Typography>
            </ListItem>
            <ListItem dense={true}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Gust:`}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {`${numeral(observation.min15.maxWindGust).format('0.00')} MPH`}
                </Typography>
            </ListItem>
            {observation.hour.hourETo > -100 && (
                <ListItem dense={true}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Hourly ETo:`}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {`${numeral(observation.hour.hourETo).format('0.000')} IN`}
                    </Typography>
                </ListItem>
            )}
        </List>
    );
};
